import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import {
    FaArrowLeft,
    FaArrowRight,
    FaArrowRight as FaArrowRightIcon,
    FaHeart,
    FaRegHeart,
    FaStar,
} from "react-icons/fa";

import {
    FaFire,
    FaTag,
    FaMapMarkerAlt,

    FaWifi,
    FaSnowflake,
    FaCoffee,
    FaSwimmingPool,
    FaUmbrellaBeach,
    FaSpa,
    FaDumbbell,
    FaParking,
    FaShuttleVan,
    FaUtensils,
    FaCocktail,
    FaConciergeBell,
    FaCheck,
    FaCalendarAlt,
    FaUsers,
    FaBed,
    FaShieldAlt,
    FaCreditCard,
    FaQuoteLeft,
    FaClock,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const API = "https://dubaihotelrates.com/api";
const HOTEL_IMG = "https://uaediscountedhotels.com/uploads/hotels";
const ROOM_IMG = "https://uaediscountedhotels.com/uploads/rooms";

const SERVICE_CHARGE = 0.10;
const VAT = 0.05;

export default function HotelDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [hotel, setHotel] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [reviews, setReviews] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [bookingLoading, setBookingLoading] = useState(false);

    const [checkIn, setCheckIn] = useState("");
    const [checkOut, setCheckOut] = useState("");
    const [guests, setGuests] = useState(1);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");

    const [wishlist, setWishlist] = useState([]);
    const [showAllAmenities, setShowAllAmenities] = useState(false);
    const [showAllReviews, setShowAllReviews] = useState(false);

    const today = new Date().toISOString().split("T")[0];

    useEffect(() => {
        if (!slug) return;

        const loadHotel = async () => {
            setLoading(true);
            setError(false);

            try {
                const [hotelRes, roomRes, reviewRes] = await Promise.all([
                    axios.get(`${API}/hotels/${slug}`),
                    axios.get(`${API}/hotels/${slug}/rooms`),
                    axios.get(`${API}/hotels/${slug}/reviews`).catch(() => ({ data: [] })),
                ]);

                setHotel(hotelRes.data);
                setRooms(Array.isArray(roomRes.data) ? roomRes.data : []);
                setReviews(Array.isArray(reviewRes.data) ? reviewRes.data : []);
            } catch (err) {
                console.error(err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        loadHotel();
    }, [slug]);

    useEffect(() => {
        try {
            setWishlist(JSON.parse(localStorage.getItem("wishlist")) || []);
        } catch {
            setWishlist([]);
        }
    }, []);

    const updateWishlist = (updated) => {
        setWishlist(updated);
        localStorage.setItem("wishlist", JSON.stringify(updated));
    };

    const isWishlisted = wishlist.some((item) => item.id === hotel?.id);

    const toggleWishlist = () => {
        if (!hotel) return;

        const updated = isWishlisted
            ? wishlist.filter((item) => item.id !== hotel.id)
            : [...wishlist, hotel];

        updateWishlist(updated);
    };

    const amenities = useMemo(() => {
        if (!hotel?.amenities) return [];

        if (Array.isArray(hotel.amenities)) {
            return hotel.amenities.filter(Boolean).map((item) => String(item).trim());
        }

        return String(hotel.amenities)
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
    }, [hotel]);

    const visibleAmenities = showAllAmenities ? amenities : amenities.slice(0, 8);
    const remainingAmenities = Math.max(amenities.length - 8, 0);

    const gallery = useMemo(() => {
        if (!hotel) return [];
        if (Array.isArray(hotel.gallery) && hotel.gallery.length) return hotel.gallery;
        return hotel.image ? [hotel.image] : [];
    }, [hotel]);

    const hotelRating = Number(hotel?.rating || 0);

    const amenityIcon = (name) => {
        const value = String(name).toLowerCase();

        if (value.includes("wifi")) return <FaWifi />;
        if (value.includes("air")) return <FaSnowflake />;
        if (value.includes("breakfast") || value.includes("coffee")) return <FaCoffee />;
        if (value.includes("pool")) return <FaSwimmingPool />;
        if (value.includes("beach")) return <FaUmbrellaBeach />;
        if (value.includes("spa")) return <FaSpa />;
        if (value.includes("gym") || value.includes("fitness")) return <FaDumbbell />;
        if (value.includes("parking")) return <FaParking />;
        if (value.includes("shuttle")) return <FaShuttleVan />;
        if (value.includes("restaurant")) return <FaUtensils />;
        if (value.includes("bar")) return <FaCocktail />;
        if (value.includes("room service")) return <FaConciergeBell />;

        return <FaCheck />;
    };

    const getRoomImages = (room) => {
        if (!room) return [];

        if (Array.isArray(room.images)) {
            return room.images.filter(Boolean);
        }

        if (room.images) {
            try {
                const parsed = JSON.parse(room.images);
                if (Array.isArray(parsed)) return parsed.filter(Boolean);
            } catch { }
        }

        return room.image ? [room.image] : [];
    };

    const getOriginalPrice = (room) =>
        Number(room?.price ?? hotel?.price_per_night ?? 0);

    const getSeasonalPrice = (date, seasonalPrices, defaultPrice) => {
        if (!Array.isArray(seasonalPrices) || !seasonalPrices.length) {
            return defaultPrice;
        }

        const selectedDate = new Date(date);

        const matched = seasonalPrices.find((season) => {
            const start = new Date(season.start_date);
            const end = new Date(season.end_date);
            return selectedDate >= start && selectedDate <= end;
        });

        return matched ? Number(matched.price) : defaultPrice;
    };

    const getBookingBasePrice = (room) => {
        if (!room) return 0;

        const selectedDate = checkIn ? new Date(checkIn) : new Date();

        const seasonalPrice = getSeasonalPrice(
            selectedDate,
            room.seasonal_prices || [],
            getOriginalPrice(room)
        );

        return room.discount_price ? Number(room.discount_price) : seasonalPrice;
    };

    const getBookingSeason = (room) => {
        if (!room) return null;

        const selectedDate = checkIn ? new Date(checkIn) : new Date();

        return room.seasonal_prices?.find((season) => {
            const start = new Date(season.start_date);
            const end = new Date(season.end_date);
            return selectedDate >= start && selectedDate <= end;
        }) || null;
    };

    const getTourismFee = () => (hotelRating === 5 ? 20 : 10);

    const getNights = () => {
        if (!checkIn || !checkOut) return 1;

        const start = new Date(checkIn);
        const end = new Date(checkOut);
        const diff = end - start;
        const nights = Math.ceil(diff / (1000 * 60 * 60 * 24));

        return nights > 0 ? nights : 1;
    };

    const getFinalRoomPrice = (room) => {
        const base = getBookingBasePrice(room);
        return Math.round(base + base * SERVICE_CHARGE + base * VAT + getTourismFee());
    };

    const getBookingTotal = () => {
        if (!selectedRoom) return 0;
        return getFinalRoomPrice(selectedRoom) * getNights();
    };

    const hasDiscount = (room) => {
        const original = getOriginalPrice(room);
        const discounted = Number(room?.discount_price || 0);
        return discounted > 0 && discounted < original;
    };

    const getRoomPricing = (room) => {
        const selectedDate = checkIn ? new Date(checkIn) : new Date();

        const seasonalBasePrice = getSeasonalPrice(
            selectedDate,
            room.seasonal_prices || [],
            getOriginalPrice(room)
        );

        const price = room.discount_price
            ? Number(room.discount_price)
            : seasonalBasePrice;

        const discounted = hasDiscount(room);
        const saved = discounted ? Math.max(seasonalBasePrice - price, 0) : 0;
        const percent =
            discounted && seasonalBasePrice
                ? Math.round((saved / seasonalBasePrice) * 100)
                : 0;

        const season = room.seasonal_prices?.find((item) => {
            const start = new Date(item.start_date);
            const end = new Date(item.end_date);
            return selectedDate >= start && selectedDate <= end;
        });

        return {
            price,
            original: seasonalBasePrice,
            saved,
            percent,
            season,
            final: getFinalRoomPrice(room),
        };
    };

    const handleBooking = async () => {
        if (!selectedRoom) {
            alert("Please select a room first.");
            return;
        }

        if (!name || !email || !phone || !checkIn || !checkOut) {
            alert("Please fill all booking fields.");
            return;
        }

        if (new Date(checkOut) <= new Date(checkIn)) {
            alert("Check-out must be after check-in.");
            return;
        }

        if (!executeRecaptcha) {
            alert("Captcha is not ready. Please refresh and try again.");
            return;
        }

        try {
            setBookingLoading(true);

            const token = await executeRecaptcha("hotel_booking");
            const activeSeason = getBookingSeason(selectedRoom);
            const user = JSON.parse(localStorage.getItem("user") || "null");

            const res = await axios.post(`${API}/hotel-payment`, {
                user_id: user?.id,
                hotel_id: hotel.id,
                room_id: selectedRoom.id,
                room_name:
                    selectedRoom.name || `Room ${selectedRoom.room_number || ""}`.trim(),
                room_price: getBookingBasePrice(selectedRoom),
                discount_price: selectedRoom.discount_price || null,
                season_name: activeSeason?.season_name || null,
                season_start: activeSeason?.start_date || null,
                season_end: activeSeason?.end_date || null,
                name,
                email,
                phone,
                check_in: checkIn,
                check_out: checkOut,
                guests: Number(guests) || 1,
                total_amount: getBookingTotal(),
                nights: getNights(),
                captcha: token,
            });

            if (res.data?.payment_url) {
                window.location.href = res.data.payment_url;
            } else {
                alert("Payment URL was not received.");
            }
        } catch (err) {
            console.error(err);
            alert(
                `❌ ${err.response?.data?.error ||
                err.response?.data?.message ||
                "Server error"
                }`
            );
        } finally {
            setBookingLoading(false);
        }
    };

    const sliderSettings = {
        dots: gallery.length > 1,
        infinite: gallery.length > 1,
        speed: 600,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: gallery.length > 1,
        autoplaySpeed: 5000,
        pauseOnHover: false,
        arrows: gallery.length > 1,
        swipe: gallery.length > 1,
        draggable: gallery.length > 1,
        touchMove: gallery.length > 1,
        adaptiveHeight: false,
        cssEase: "ease-in-out",
    };

    const roomSliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        arrows: false,
        autoplay: false,
        dotsClass: "slick-dots !bottom-3",
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F4F8FC]">
                <div className="h-[420px] animate-pulse bg-slate-200" />
                <div className="max-w-7xl mx-auto px-4 py-10">
                    <div className="grid lg:grid-cols-[1fr_390px] gap-8">
                        <div className="space-y-5">
                            <div className="h-10 w-2/3 bg-slate-200 rounded-xl animate-pulse" />
                            <div className="h-32 bg-slate-200 rounded-2xl animate-pulse" />
                            <div className="h-72 bg-slate-200 rounded-2xl animate-pulse" />
                        </div>
                        <div className="h-[600px] bg-slate-200 rounded-3xl animate-pulse" />
                    </div>
                </div>
            </div>
        );
    }

    if (error || !hotel) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F4F8FC] px-4">
                <div className="max-w-md text-center bg-white rounded-3xl p-10 shadow-xl border border-slate-100">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-red-50 text-red-500 flex items-center justify-center text-2xl">
                        !
                    </div>
                    <h1 className="text-2xl font-bold text-[#0F172A] mt-5">
                        Hotel not found
                    </h1>
                    <p className="text-slate-500 mt-2">
                        We could not load this hotel. Please return to the hotel listing.
                    </p>
                    <button
                        onClick={() => navigate("/hotels")}
                        className="mt-6 px-6 py-3 rounded-xl bg-[#174EA6] text-white font-semibold hover:bg-[#123D85] transition"
                    >
                        Browse Hotels
                    </button>
                </div>
            </div>
        );
    }

    const selectedSeason = selectedRoom ? getBookingSeason(selectedRoom) : null;
    const selectedBase = selectedRoom ? getBookingBasePrice(selectedRoom) : 0;
    const selectedFinal = selectedRoom ? getFinalRoomPrice(selectedRoom) : 0;

    return (
        <>
            <Helmet>
                <title>{hotel.meta_title || hotel.name}</title>
                <meta
                    name="description"
                    content={hotel.meta_description || hotel.description || ""}
                />
                <link
                    rel="canonical"
                    href={`https://dubaihotelrates.com/hotels/${hotel.slug}`}
                />
                <meta property="og:title" content={hotel.meta_title || hotel.name} />
                <meta
                    property="og:description"
                    content={hotel.meta_description || hotel.description || ""}
                />
                <meta
                    property="og:url"
                    content={`https://dubaihotelrates.com/hotels/${hotel.slug}`}
                />
                <meta
                    property="og:image"
                    content={
                        hotel.image
                            ? `${HOTEL_IMG}/${hotel.image}`
                            : "https://dubaihotelrates.com/images/default-og.png"
                    }
                />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={hotel.meta_title || hotel.name} />
                <meta
                    name="twitter:description"
                    content={hotel.meta_description || hotel.description || ""}
                />
                <meta
                    name="twitter:image"
                    content={
                        hotel.image
                            ? `${HOTEL_IMG}/${hotel.image}`
                            : "https://dubaihotelrates.com/images/default-og.png"
                    }
                />
                <meta name="robots" content="index, follow" />
            </Helmet>

            <main className="bg-[#F4F8FC] text-[#0F172A] min-h-screen pb-20">


                <section className="relative w-full overflow-hidden">

                    <div className="relative h-[390px] w-full sm:h-[430px] md:h-[470px] lg:h-[510px] xl:h-[540px]">

                        {/* =====================================================
            HOTEL IMAGE SLIDER
        ===================================================== */}

                        {gallery.length > 0 ? (
                            <div className="absolute inset-0 z-0">

                                <Slider
                                    {...sliderSettings}
                                    className="hotel-detail-slider h-full"
                                >
                                    {gallery.map((img, index) => (
                                        <div
                                            key={`${img}-${index}`}
                                            className="relative h-[390px] outline-none sm:h-[430px] md:h-[470px] lg:h-[510px] xl:h-[540px]"
                                        >
                                            <img
                                                src={`${HOTEL_IMG}/${img}`}
                                                alt={`${hotel.name} ${index + 1}`}
                                                className="h-full w-full object-cover object-center"
                                            />
                                        </div>
                                    ))}
                                </Slider>

                            </div>
                        ) : (
                            <div className="absolute inset-0 z-0 flex items-center justify-center bg-[#002B45]">
                                <p className="text-sm text-white/70">
                                    No hotel images available
                                </p>
                            </div>
                        )}


                        {/* =====================================================
            IMAGE OVERLAY
        ===================================================== */}

                        <div
                            className="
                pointer-events-none
                absolute
                inset-0
                z-[2]
                bg-gradient-to-r
                from-[#031F4F]/85
                via-[#073D82]/45
                to-black/10
            "
                        />

                        <div
                            className="
                pointer-events-none
                absolute
                inset-x-0
                bottom-0
                z-[2]
                h-72
                bg-gradient-to-t
                from-[#031F4F]/90
                via-[#031F4F]/40
                to-transparent
            "
                        />

                        <div
                            className="
                pointer-events-none
                absolute
                inset-x-0
                top-0
                z-[2]
                h-40
                bg-gradient-to-b
                from-[#062D67]/55
                to-transparent
            "
                        />


                        {/* =====================================================
            HOTEL CONTENT
        ===================================================== */}

                        <div
                            className="
                pointer-events-none
                absolute
                inset-0
                z-10
                flex
                items-end
            "
                        >

                            <div
                                className="
                    mx-auto
                    w-full
                    max-w-[1440px]
                    px-5
                    pb-12
                    sm:px-8
                    sm:pb-14
                    lg:px-10
                    lg:pb-16
                    xl:px-12
                "
                            >

                                <div className="max-w-[850px] text-white">

                                    {/* Rating badges */}

                                    <div className="mb-3 flex flex-wrap items-center gap-2">

                                        {hotelRating > 0 && (
                                            <div
                                                className="
                                    inline-flex
                                    items-center
                                    gap-1.5
                                    rounded-md
                                    bg-[#FFC928]
                                    px-3
                                    py-1.5
                                    text-xs
                                    font-extrabold
                                    text-[#0F172A]
                                    shadow-lg
                                "
                                            >
                                                <FaStar />

                                                {hotelRating.toFixed(1)}
                                            </div>
                                        )}

                                        {hotelRating > 0 && (
                                            <div
                                                className="
                                    inline-flex
                                    items-center
                                    rounded-md
                                    bg-[#174EA6]/90
                                    px-3
                                    py-1.5
                                    text-[10px]
                                    font-bold
                                    uppercase
                                    tracking-wide
                                    text-white
                                    shadow-lg
                                    backdrop-blur-sm
                                "
                                            >
                                                {hotelRating}-Star Hotel
                                            </div>
                                        )}

                                        <div
                                            className="
                                inline-flex
                                items-center
                                rounded-md
                                border
                                border-white/20
                                bg-black/30
                                px-3
                                py-1.5
                                text-[10px]
                                font-bold
                                uppercase
                                tracking-wide
                                text-white
                                backdrop-blur-md
                            "
                                        >
                                            Dubai Hotel Rates
                                        </div>

                                    </div>


                                    {/* Hotel name */}

                                    <h1
                                        className="
                            text-3xl
                            font-extrabold
                            leading-[1.05]
                            tracking-tight
                            text-white
                            drop-shadow-[0_4px_15px_rgba(0,0,0,0.35)]
                            sm:text-4xl
                            md:text-5xl
                            lg:text-6xl
                            xl:text-[60px]
                        "
                                    >
                                        {hotel.name}
                                    </h1>


                                    {/* Location */}

                                    {hotel.location && (
                                        <a
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                                hotel.location
                                            )}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="
                                pointer-events-auto
                                mt-3
                                inline-flex
                                items-center
                                gap-2
                                text-sm
                                font-medium
                                text-white/90
                                transition
                                hover:text-[#FFC928]
                                sm:text-base
                            "
                                        >
                                            <FaMapMarkerAlt className="shrink-0 text-[#FFC928]" />

                                            <span>
                                                {hotel.location}
                                            </span>

                                            <FaArrowRightIcon className="ml-1 text-xs" />
                                        </a>
                                    )}


                                    {/* Description */}

                                    {hotel.description && (
                                        <p
                                            className="
                                mt-3
                                max-w-[680px]
                                line-clamp-2
                                text-sm
                                leading-6
                                text-white/85
                                drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]
                                sm:text-base
                                sm:leading-7
                            "
                                        >
                                            {hotel.description
                                                .replace(/<[^>]+>/g, "")
                                                .trim()}
                                        </p>
                                    )}

                                </div>

                            </div>

                        </div>


                        {/* =====================================================
            WISHLIST
        ===================================================== */}

                        <button
                            type="button"
                            onClick={toggleWishlist}
                            aria-label="Save hotel"
                            className="
                absolute
                right-5
                top-5
                z-30
                flex
                h-12
                w-12
                items-center
                justify-center
                rounded-full
                bg-white/95
                shadow-[0_10px_30px_rgba(0,0,0,0.25)]
                backdrop-blur-md
                transition-all
                duration-300
                hover:scale-110
                sm:right-8
                sm:top-8
                lg:right-10
                lg:top-10
            "
                        >
                            {isWishlisted ? (
                                <FaHeart className="text-lg text-red-500" />
                            ) : (
                                <FaRegHeart className="text-lg text-[#0F172A]" />
                            )}
                        </button>

                    </div>

                </section>


                {/* =========================================================
    QUICK HOTEL INFORMATION
========================================================= */}

                <section
                    className="
        relative
        z-20

        -mt-8
        sm:-mt-10

        px-4
        sm:px-6
        lg:px-8
    "
                >
                    <div
                        className="
            mx-auto
            max-w-7xl

            overflow-hidden

            rounded-2xl
            sm:rounded-[22px]

            border
            border-white/60

            bg-white

            shadow-[0_20px_50px_rgba(15,23,42,0.15)]
        "
                    >

                        <div
                            className="
                grid

                grid-cols-2

                lg:grid-cols-4
            "
                        >

                            {/* LOCATION */}

                            <div
                                className="
                    flex
                    items-center
                    gap-3

                    p-4
                    sm:p-5

                    border-b
                    lg:border-b-0

                    border-r
                    border-slate-100
                "
                            >
                                <div
                                    className="
                        flex
                        h-11
                        w-11
                        shrink-0
                        items-center
                        justify-center

                        rounded-xl

                        bg-[#EAF3FF]
                        text-[#174EA6]
                    "
                                >
                                    <FaMapMarkerAlt />
                                </div>

                                <div className="min-w-0">
                                    <p
                                        className="
                            text-[9px]
                            uppercase
                            tracking-[0.16em]
                            font-bold
                            text-slate-400
                        "
                                    >
                                        Location
                                    </p>

                                    <p
                                        className="
                            mt-1
                            truncate

                            text-xs
                            sm:text-sm

                            font-bold
                            text-[#0F172A]
                        "
                                    >
                                        {hotel.location || "Dubai, UAE"}
                                    </p>
                                </div>
                            </div>


                            {/* ROOMS */}

                            <div
                                className="
                    flex
                    items-center
                    gap-3

                    p-4
                    sm:p-5

                    border-b
                    lg:border-b-0

                    lg:border-r

                    border-slate-100
                "
                            >
                                <div
                                    className="
                        flex
                        h-11
                        w-11
                        shrink-0
                        items-center
                        justify-center

                        rounded-xl

                        bg-[#FFF7D6]
                        text-[#174EA6]
                    "
                                >
                                    <FaBed />
                                </div>

                                <div>
                                    <p
                                        className="
                            text-[9px]
                            uppercase
                            tracking-[0.16em]
                            font-bold
                            text-slate-400
                        "
                                    >
                                        Rooms
                                    </p>

                                    <p
                                        className="
                            mt-1
                            text-xs
                            sm:text-sm
                            font-bold
                            text-[#0F172A]
                        "
                                    >
                                        {rooms.length} available
                                    </p>
                                </div>
                            </div>


                            {/* GUESTS */}

                            <div
                                className="
                    flex
                    items-center
                    gap-3

                    p-4
                    sm:p-5

                    border-r
                    border-slate-100
                "
                            >
                                <div
                                    className="
                        flex
                        h-11
                        w-11
                        shrink-0
                        items-center
                        justify-center

                        rounded-xl

                        bg-[#EAF3FF]
                        text-[#174EA6]
                    "
                                >
                                    <FaUsers />
                                </div>

                                <div>
                                    <p
                                        className="
                            text-[9px]
                            uppercase
                            tracking-[0.16em]
                            font-bold
                            text-slate-400
                        "
                                    >
                                        Guests
                                    </p>

                                    <p
                                        className="
                            mt-1
                            text-xs
                            sm:text-sm
                            font-bold
                            text-[#0F172A]
                        "
                                    >
                                        {guests} guest{guests !== 1 ? "s" : ""}
                                    </p>
                                </div>
                            </div>


                            {/* SECURE BOOKING */}

                            <div
                                className="
                    flex
                    items-center
                    gap-3

                    p-4
                    sm:p-5
                "
                            >
                                <div
                                    className="
                        flex
                        h-11
                        w-11
                        shrink-0
                        items-center
                        justify-center

                        rounded-xl

                        bg-[#FFF7D6]
                        text-[#174EA6]
                    "
                                >
                                    <FaShieldAlt />
                                </div>

                                <div>
                                    <p
                                        className="
                            text-[9px]
                            uppercase
                            tracking-[0.16em]
                            font-bold
                            text-slate-400
                        "
                                    >
                                        Booking
                                    </p>

                                    <p
                                        className="
                            mt-1
                            text-xs
                            sm:text-sm
                            font-bold
                            text-[#0F172A]
                        "
                                    >
                                        Secure & trusted
                                    </p>
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                {/* MAIN */}
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
                    <div className="grid lg:grid-cols-[minmax(0,1fr)_390px] gap-8 items-start">
                        {/* LEFT */}
                        <div className="space-y-7">
                            {/* ABOUT */}
                            <section className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                                <div className="p-6 sm:p-8">
                                    <div className="flex items-start gap-4 mb-6">
                                        <div className="w-12 h-12 rounded-2xl bg-[#174EA6] text-white flex items-center justify-center shrink-0 shadow-lg">
                                            <FaHotelIcon />
                                        </div>
                                        <div>
                                            <p className="text-[11px] uppercase tracking-[0.22em] text-[#174EA6] font-bold">
                                                Your stay
                                            </p>
                                            <h2 className="text-2xl sm:text-3xl font-extrabold mt-1">
                                                About this hotel
                                            </h2>
                                        </div>
                                    </div>

                                    <div
                                        className="hotel-content prose prose-slate max-w-none text-slate-600 leading-7"
                                        dangerouslySetInnerHTML={{
                                            __html: hotel.description || "A comfortable stay in Dubai.",
                                        }}
                                    />
                                </div>

                                {amenities.length > 0 && (
                                    <div className="border-t border-slate-100 bg-[#FBFDFF] p-6 sm:p-8">
                                        <div className="flex items-end justify-between gap-4 mb-5">
                                            <div>
                                                <p className="text-[11px] uppercase tracking-[0.22em] text-[#174EA6] font-bold">
                                                    Hotel facilities
                                                </p>
                                                <h3 className="text-xl font-bold mt-1">
                                                    Everything you need
                                                </h3>
                                            </div>

                                            {amenities.length > 8 && (
                                                <button
                                                    onClick={() =>
                                                        setShowAllAmenities(!showAllAmenities)
                                                    }
                                                    className="text-sm font-bold text-[#174EA6] hover:text-[#123D85]"
                                                >
                                                    {showAllAmenities
                                                        ? "Show less"
                                                        : `View all ${amenities.length}`}
                                                </button>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                            {visibleAmenities.map((amenity, index) => (
                                                <div
                                                    key={`${amenity}-${index}`}
                                                    className="group flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-3.5 hover:border-[#174EA6]/30 hover:shadow-md transition"
                                                >
                                                    <div className="w-10 h-10 rounded-xl bg-[#EAF3FF] text-[#174EA6] flex items-center justify-center shrink-0 group-hover:bg-[#174EA6] group-hover:text-white transition">
                                                        {amenityIcon(amenity)}
                                                    </div>
                                                    <span className="text-xs sm:text-sm font-semibold text-slate-700">
                                                        {amenity}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {(hotel.check_in_time || hotel.check_out_time) && (
                                    <div className="border-t border-slate-100 p-6 sm:p-8 grid sm:grid-cols-2 gap-3">
                                        {hotel.check_in_time && (
                                            <div className="flex items-center gap-3 rounded-2xl bg-[#F4F8FC] p-4">
                                                <FaClock className="text-[#174EA6]" />
                                                <div>
                                                    <p className="text-xs text-slate-400">Check-in</p>
                                                    <p className="font-semibold">{hotel.check_in_time}</p>
                                                </div>
                                            </div>
                                        )}
                                        {hotel.check_out_time && (
                                            <div className="flex items-center gap-3 rounded-2xl bg-[#F4F8FC] p-4">
                                                <FaClock className="text-[#174EA6]" />
                                                <div>
                                                    <p className="text-xs text-slate-400">Check-out</p>
                                                    <p className="font-semibold">{hotel.check_out_time}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </section>

                            {/* ROOMS */}
                            <section id="rooms" className="scroll-mt-24">
                                <div className="flex items-end justify-between gap-4 mb-5">
                                    <div>
                                        <p className="text-[11px] uppercase tracking-[0.22em] text-[#174EA6] font-bold">
                                            Choose your room
                                        </p>
                                        <h2 className="text-2xl sm:text-3xl font-extrabold mt-1">
                                            Available rooms
                                        </h2>
                                    </div>
                                    <span className="hidden sm:block text-sm text-slate-500">
                                        {rooms.length} option{rooms.length !== 1 ? "s" : ""}
                                    </span>
                                </div>

                                <div className="space-y-5">
                                    {rooms.map((room) => {
                                        const images = getRoomImages(room);
                                        const pricing = getRoomPricing(room);
                                        const isSelected = selectedRoom?.id === room.id;

                                        return (
                                            <article
                                                key={room.id}
                                                onClick={() => setSelectedRoom(room)}
                                                className={`bg-white rounded-3xl overflow-hidden border shadow-sm cursor-pointer transition-all duration-300 ${isSelected
                                                    ? "border-[#174EA6] ring-4 ring-[#174EA6]/10 shadow-xl"
                                                    : "border-slate-100 hover:border-[#174EA6]/30 hover:shadow-xl"
                                                    }`}
                                            >
                                                <div className="grid lg:grid-cols-[330px_minmax(0,1fr)]">
                                                    <div className="relative h-60 lg:h-full min-h-[260px] overflow-hidden bg-slate-100">
                                                        {images.length ? (
                                                            <Slider {...roomSliderSettings}>
                                                                {images.map((img, index) => (
                                                                    <div key={index}>
                                                                        <img
                                                                            src={`${ROOM_IMG}/${img}`}
                                                                            alt={room.name || `Room ${index + 1}`}
                                                                            className="w-full h-60 lg:h-[300px] object-cover"
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </Slider>
                                                        ) : (
                                                            <div className="h-full flex items-center justify-center text-slate-400">
                                                                No room image
                                                            </div>
                                                        )}

                                                        <div className="absolute top-4 left-4 z-10 flex flex-wrap gap-2">
                                                            {pricing.season && (
                                                                <span className="inline-flex items-center gap-1.5 bg-[#FFF7D6] text-[#7A5B00] border border-[#FFC928]/50 px-3 py-1.5 rounded-full text-[10px] font-bold shadow">
                                                                    <FaFire className="text-[#E9A900]" />
                                                                    Seasonal offer
                                                                </span>
                                                            )}
                                                            {pricing.percent > 0 && (
                                                                <span className="inline-flex items-center gap-1.5 bg-emerald-500 text-white px-3 py-1.5 rounded-full text-[10px] font-bold shadow">
                                                                    <FaTag />
                                                                    {pricing.percent}% OFF
                                                                </span>
                                                            )}
                                                        </div>

                                                        {isSelected && (
                                                            <div className="absolute bottom-4 left-4 z-10 bg-[#174EA6] text-white px-3 py-1.5 rounded-full text-xs font-bold shadow">
                                                                Selected
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="p-5 sm:p-6 flex flex-col justify-between">
                                                        <div>
                                                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                                                <div>
                                                                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#174EA6] font-bold">
                                                                        {room.room_number
                                                                            ? `Room ${room.room_number}`
                                                                            : "Premium room"}
                                                                    </p>
                                                                    <h3 className="text-xl font-extrabold mt-1">
                                                                        {room.name || `Room ${room.room_number || ""}`}
                                                                    </h3>

                                                                    <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-500">
                                                                        <span className="inline-flex items-center gap-1.5">
                                                                            <FaUsers className="text-[#174EA6]" />
                                                                            {room.capacity || 1} guests
                                                                        </span>
                                                                        <span className="inline-flex items-center gap-1.5">
                                                                            <FaBed className="text-[#174EA6]" />
                                                                            {room.beds_count
                                                                                ? `${room.beds_count} beds`
                                                                                : "1 bed"}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <div className="sm:text-right shrink-0">
                                                                    <div className="flex items-end sm:justify-end gap-1">
                                                                        <span className="text-2xl font-extrabold text-[#174EA6]">
                                                                            {pricing.price.toLocaleString()}
                                                                        </span>
                                                                        <span className="text-xs text-slate-400 mb-1">
                                                                            AED / night
                                                                        </span>
                                                                    </div>

                                                                    {pricing.percent > 0 && (
                                                                        <>
                                                                            <p className="text-xs text-slate-400 line-through">
                                                                                {pricing.original.toLocaleString()} AED
                                                                            </p>
                                                                            <p className="text-xs font-bold text-emerald-600 mt-1">
                                                                                Save {pricing.saved.toLocaleString()} AED
                                                                            </p>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="flex flex-wrap gap-2 mt-5">
                                                                {[
                                                                    ["WiFi", <FaWifi />],
                                                                    ["Air conditioning", <FaSnowflake />],
                                                                    ["Breakfast", <FaCoffee />],
                                                                ].map(([label, icon]) => (
                                                                    <span
                                                                        key={label}
                                                                        className="inline-flex items-center gap-1.5 rounded-full bg-[#F3F8FF] border border-[#E1EDFF] px-3 py-1.5 text-xs font-medium text-slate-600"
                                                                    >
                                                                        <span className="text-[#174EA6]">{icon}</span>
                                                                        {label}
                                                                    </span>
                                                                ))}
                                                            </div>

                                                            {pricing.season && (
                                                                <div className="mt-4 rounded-2xl bg-[#FFF9E8] border border-[#FFC928]/30 p-3">
                                                                    <div className="flex gap-2">
                                                                        <FaCalendarAlt className="text-[#D89E00] mt-0.5" />
                                                                        <div>
                                                                            <p className="text-xs font-bold text-[#806000]">
                                                                                {pricing.season.season_name} pricing
                                                                            </p>
                                                                            <p className="text-[11px] text-[#9A7A25] mt-0.5">
                                                                                {pricing.season.start_date} → {pricing.season.end_date}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="grid sm:grid-cols-2 gap-3 mt-6">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/hotels/${hotel.slug}/${room.id}`);
                                                                }}
                                                                className="rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-700 hover:border-[#174EA6] hover:text-[#174EA6] transition"
                                                            >
                                                                View room details
                                                            </button>

                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedRoom(room);
                                                                    document
                                                                        .getElementById("booking-panel")
                                                                        ?.scrollIntoView({
                                                                            behavior: "smooth",
                                                                            block: "start",
                                                                        });
                                                                }}
                                                                className={`rounded-xl py-3 text-sm font-bold transition ${isSelected
                                                                    ? "bg-[#174EA6] text-white shadow-lg"
                                                                    : "bg-[#FFC928] text-[#0F172A] hover:bg-[#FFD43B] shadow-md"
                                                                    }`}
                                                            >
                                                                {isSelected ? "Room selected ✓" : "Select this room"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </article>
                                        );
                                    })}

                                    {!rooms.length && (
                                        <div className="bg-white border border-slate-100 rounded-3xl p-12 text-center">
                                            <FaBed className="mx-auto text-3xl text-slate-300" />
                                            <h3 className="font-bold text-lg mt-4">
                                                No rooms available
                                            </h3>
                                            <p className="text-sm text-slate-500 mt-1">
                                                Please check again later.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* REVIEWS */}
                            <section className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                                <div className="p-6 sm:p-8 border-b border-slate-100">
                                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                                        <div>
                                            <p className="text-[11px] uppercase tracking-[0.22em] text-[#174EA6] font-bold">
                                                Guest experience
                                            </p>
                                            <h2 className="text-2xl sm:text-3xl font-extrabold mt-1">
                                                Guest reviews
                                            </h2>
                                            <p className="text-sm text-slate-500 mt-2">
                                                Honest feedback from guests who stayed here.
                                            </p>
                                        </div>

                                        {reviews.length > 0 && (
                                            <div className="rounded-2xl bg-[#FFF9E8] border border-[#FFC928]/30 px-5 py-3">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-2xl font-extrabold">
                                                        {(
                                                            reviews.reduce(
                                                                (sum, review) => sum + Number(review.rating || 0),
                                                                0
                                                            ) / reviews.length
                                                        ).toFixed(1)}
                                                    </span>
                                                    <div>
                                                        <div className="text-[#F4B400] tracking-tight">
                                                            ★★★★★
                                                        </div>
                                                        <p className="text-[10px] text-slate-500">
                                                            {reviews.length} review{reviews.length !== 1 ? "s" : ""}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="p-6 sm:p-8">
                                    {reviews.length === 0 ? (
                                        <div className="text-center py-14">
                                            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#FFF9E8] text-[#E4A900] flex items-center justify-center">
                                                <FaStar className="text-2xl" />
                                            </div>
                                            <h3 className="font-bold text-lg mt-4">
                                                No reviews yet
                                            </h3>
                                            <p className="text-sm text-slate-500 mt-1">
                                                Be the first guest to share your experience.
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="space-y-4">
                                                {(showAllReviews
                                                    ? reviews
                                                    : reviews.slice(0, 4)
                                                ).map((review) => {
                                                    const rawName =
                                                        review.email?.split("@")[0] || "Guest";
                                                    const displayName = rawName
                                                        .replace(/\./g, " ")
                                                        .replace(/\b\w/g, (letter) =>
                                                            letter.toUpperCase()
                                                        );

                                                    const rating = Number(review.rating || 0);

                                                    return (
                                                        <article
                                                            key={review.id}
                                                            className="rounded-2xl border border-slate-100 p-5 hover:border-[#174EA6]/20 hover:shadow-md transition"
                                                        >
                                                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-11 h-11 rounded-full bg-[#174EA6] text-white flex items-center justify-center font-bold">
                                                                        {displayName.charAt(0)}
                                                                    </div>
                                                                    <div>
                                                                        <h4 className="font-bold text-sm">
                                                                            {displayName}
                                                                        </h4>
                                                                        <div className="flex items-center gap-2 mt-1">
                                                                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                                                                                Verified Guest
                                                                            </span>
                                                                            <span className="text-[11px] text-slate-400">
                                                                                {dayjs(review.created_at).fromNow()}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="inline-flex items-center gap-2 bg-[#FFF9E8] border border-[#FFC928]/30 px-3 py-2 rounded-xl">
                                                                    <span className="font-extrabold">
                                                                        {rating.toFixed(1)}
                                                                    </span>
                                                                    <span className="text-[#F4B400] text-sm">
                                                                        {"★".repeat(Math.min(rating, 5))}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="mt-5 pt-4 border-t border-slate-100 flex gap-3">
                                                                <FaQuoteLeft className="text-[#FFC928] mt-1 shrink-0" />
                                                                <p className="text-sm text-slate-600 leading-7">
                                                                    {review.comment}
                                                                </p>
                                                            </div>
                                                        </article>
                                                    );
                                                })}
                                            </div>

                                            {reviews.length > 4 && (
                                                <button
                                                    onClick={() => setShowAllReviews(!showAllReviews)}
                                                    className="mt-6 w-full rounded-xl border border-[#174EA6]/20 py-3 text-sm font-bold text-[#174EA6] hover:bg-[#F3F8FF] transition"
                                                >
                                                    {showAllReviews
                                                        ? "Show fewer reviews"
                                                        : `View all ${reviews.length} reviews`}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </section>
                        </div>

                        {/* BOOKING PANEL */}
                        <aside id="booking-panel" className="lg:sticky lg:top-24 scroll-mt-24">
                            <div className="bg-white rounded-3xl border border-slate-100 shadow-2xl overflow-hidden">
                                <div className="bg-[#002B45] p-6 text-white relative overflow-hidden">
                                    <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-[#174EA6]/40 blur-2xl" />
                                    <div className="absolute -right-4 -bottom-12 w-28 h-28 rounded-full bg-[#FFC928]/30 blur-2xl" />

                                    <div className="relative">
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#FFC928] font-bold">
                                            Reserve your stay
                                        </p>
                                        <h2 className="!text-white text-2xl font-extrabold mt-1">
                                            Book this hotel
                                        </h2>
                                        <p className="text-xs text-white/65 mt-2">
                                            Secure checkout with instant confirmation.
                                        </p>
                                    </div>
                                </div>

                                <div className="p-5 sm:p-6 space-y-4">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                                                Check-in
                                            </label>
                                            <div className="relative mt-1">
                                                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-[#174EA6] text-xs pointer-events-none" />
                                                <input
                                                    type="date"
                                                    value={checkIn}
                                                    min={today}
                                                    onChange={(e) => {
                                                        setCheckIn(e.target.value);
                                                        if (
                                                            checkOut &&
                                                            e.target.value >= checkOut
                                                        ) {
                                                            setCheckOut("");
                                                        }
                                                    }}
                                                    className="w-full pl-8 pr-2 py-3 rounded-xl border border-slate-200 bg-white text-xs outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                                                Check-out
                                            </label>
                                            <div className="relative mt-1">
                                                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-[#174EA6] text-xs pointer-events-none" />
                                                <input
                                                    type="date"
                                                    value={checkOut}
                                                    min={checkIn || today}
                                                    onChange={(e) => setCheckOut(e.target.value)}
                                                    className="w-full pl-8 pr-2 py-3 rounded-xl border border-slate-200 bg-white text-xs outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                                            Guests
                                        </label>
                                        <div className="relative mt-1">
                                            <FaUsers className="absolute left-3 top-1/2 -translate-y-1/2 text-[#174EA6] text-xs pointer-events-none" />
                                            <input
                                                type="number"
                                                min="1"
                                                value={guests}
                                                onChange={(e) =>
                                                    setGuests(Math.max(1, Number(e.target.value) || 1))
                                                }
                                                className="w-full pl-8 pr-3 py-3 rounded-xl border border-slate-200 outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10 text-sm"
                                            />
                                        </div>
                                    </div>

                                    <div className="h-px bg-slate-100" />

                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#174EA6] font-bold">
                                            Guest details
                                        </p>

                                        <div className="space-y-3 mt-3">
                                            <input
                                                type="text"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                placeholder="Full name"
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10 text-sm"
                                            />

                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="Email address"
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10 text-sm"
                                            />

                                            <input
                                                type="tel"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                placeholder="+971 4 447 3839"
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10 text-sm"
                                            />
                                        </div>
                                    </div>

                                    {!selectedRoom ? (
                                        <div className="rounded-2xl bg-[#F3F8FF] border border-[#DCEAFF] p-4">
                                            <div className="flex gap-3">
                                                <FaBed className="text-[#174EA6] mt-0.5" />
                                                <div>
                                                    <p className="text-sm font-bold">
                                                        Select a room to continue
                                                    </p>
                                                    <p className="text-xs text-slate-500 mt-1">
                                                        Choose one of the rooms below and the full price will appear here.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="rounded-2xl bg-[#F8FBFF] border border-[#DCEAFF] overflow-hidden">
                                            <div className="p-4 border-b border-[#DCEAFF]">
                                                <p className="text-[10px] uppercase tracking-wider text-[#174EA6] font-bold">
                                                    Selected room
                                                </p>
                                                <p className="font-bold mt-1">
                                                    {selectedRoom.name ||
                                                        `Room ${selectedRoom.room_number || ""}`}
                                                </p>
                                            </div>

                                            <div className="p-4 space-y-3">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-500">
                                                        Base / night
                                                    </span>
                                                    <span className="font-semibold">
                                                        {selectedBase.toLocaleString()} AED
                                                    </span>
                                                </div>

                                                {selectedSeason && (
                                                    <div className="rounded-xl bg-[#FFF9E8] border border-[#FFC928]/30 p-3">
                                                        <p className="text-xs font-bold text-[#806000]">
                                                            {selectedSeason.season_name} pricing applied
                                                        </p>
                                                        <p className="text-[10px] text-[#9A7A25] mt-1">
                                                            {selectedSeason.start_date} → {selectedSeason.end_date}
                                                        </p>
                                                    </div>
                                                )}

                                                <div className="flex justify-between text-xs text-slate-500">
                                                    <span>Service charge (10%)</span>
                                                    <span>
                                                        {(selectedBase * SERVICE_CHARGE).toLocaleString(undefined, {
                                                            maximumFractionDigits: 0,
                                                        })}{" "}
                                                        AED
                                                    </span>
                                                </div>

                                                <div className="flex justify-between text-xs text-slate-500">
                                                    <span>VAT (5%)</span>
                                                    <span>
                                                        {(selectedBase * VAT).toLocaleString(undefined, {
                                                            maximumFractionDigits: 0,
                                                        })}{" "}
                                                        AED
                                                    </span>
                                                </div>

                                                <div className="flex justify-between text-xs text-slate-500">
                                                    <span>Tourism fee</span>
                                                    <span>{getTourismFee()} AED</span>
                                                </div>

                                                <div className="border-t border-[#DCEAFF] pt-3 flex justify-between">
                                                    <span className="font-bold text-sm">
                                                        Final / night
                                                    </span>
                                                    <span className="font-extrabold text-[#174EA6]">
                                                        {selectedFinal.toLocaleString()} AED
                                                    </span>
                                                </div>

                                                {checkIn && checkOut && (
                                                    <div className="rounded-xl bg-white border border-slate-100 p-3">
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-slate-500">
                                                                {getNights()} night
                                                                {getNights() !== 1 ? "s" : ""}
                                                            </span>
                                                            <span className="font-extrabold">
                                                                {getBookingTotal().toLocaleString()} AED
                                                            </span>
                                                        </div>
                                                        <p className="text-[10px] text-slate-400 mt-1">
                                                            Taxes and fees included
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <button
                                        onClick={handleBooking}
                                        disabled={!selectedRoom || bookingLoading}
                                        className="w-full py-4 rounded-xl bg-[#174EA6] text-white font-extrabold text-sm shadow-lg shadow-[#174EA6]/20 hover:bg-[#123D85] hover:-translate-y-0.5 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                                    >
                                        {bookingLoading ? (
                                            <>
                                                <span className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                Confirm & Book Now
                                                <FaArrowRight />
                                            </>
                                        )}
                                    </button>

                                    <div className="flex items-center justify-center gap-2 text-[10px] text-slate-400">
                                        <FaLockIcon />
                                        Secure payment • Instant confirmation
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-3">
                                <div className="bg-white rounded-2xl border border-slate-100 p-4 text-center">
                                    <FaShieldAlt className="mx-auto text-[#174EA6]" />
                                    <p className="text-xs font-bold mt-2">Trusted booking</p>
                                </div>
                                <div className="bg-white rounded-2xl border border-slate-100 p-4 text-center">
                                    <FaCreditCard className="mx-auto text-[#174EA6]" />
                                    <p className="text-xs font-bold mt-2">Secure payment</p>
                                </div>
                            </div>
                        </aside>
                    </div>
                </section>
            </main>

            {/* MOBILE RESERVE BAR */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-t border-slate-200 p-3 shadow-[0_-8px_30px_rgba(15,23,42,0.12)]">
                <div className="max-w-2xl mx-auto flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                            {selectedRoom ? "Selected room" : "Starting from"}
                        </p>
                        <p className="font-extrabold text-[#174EA6] truncate">
                            {selectedRoom
                                ? `${getFinalRoomPrice(selectedRoom).toLocaleString()} AED / night`
                                : rooms.length
                                    ? `${Math.min(
                                        ...rooms.map((room) => getFinalRoomPrice(room))
                                    ).toLocaleString()} AED / night`
                                    : "Check rooms"}
                        </p>
                    </div>

                    <button
                        onClick={() => {
                            if (!selectedRoom) {
                                document
                                    .getElementById("rooms")
                                    ?.scrollIntoView({ behavior: "smooth" });
                            } else {
                                document
                                    .getElementById("booking-panel")
                                    ?.scrollIntoView({ behavior: "smooth" });
                            }
                        }}
                        className="px-5 py-3 rounded-xl bg-[#FFC928] text-[#0F172A] font-extrabold text-sm shadow-lg"
                    >
                        {selectedRoom ? "Reserve" : "Choose Room"}
                    </button>
                </div>
            </div>
        </>
    );
}

/* Small local icon wrappers keep the JSX readable without introducing
   any dependency beyond react-icons/fa. */
function FaHotelIcon(props) {
    return <FaConciergeBell {...props} />;
}

function FaLockIcon(props) {
    return <FaShieldAlt {...props} />;
}
